import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import ModalityChip from '~/components/Chip/ModalityChip';

export type DatasetSummary = {
    /** Per-modality dataset counts across ALL of the patient's series. */
    composition: Array<{ modality: string; count: number }>;
    studyCount: number;
    datasetCount: number;
};

type CompositionFooterProps = {
    /** What the record holds; undefined while that is not yet known, and nothing is claimed. */
    summary: DatasetSummary | undefined;
    /** Controls pinned to the footer's right-hand end. */
    actions?: ReactNode;
};

/**
 * Panel footer summarizing the patient's datasets per modality, with the
 * panel actions at its end.
 */
export default function CompositionFooter({
    summary,
    actions,
}: CompositionFooterProps) {
    const intl = useIntl();
    return (
        <Box
            sx={{
                py: 1.75,
                px: 3,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.raised',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                }}
            >
                {summary && (
                    <>
                        {/* Chips scroll horizontally when they overflow; the totals stay pinned right. */}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                overflowX: 'auto',
                                '& > *': { flexShrink: 0 },
                                '& > :last-child': { mr: '24px' },
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': { display: 'none' },
                                maskImage:
                                    'linear-gradient(to right, #000 calc(100% - 24px), transparent)',
                                WebkitMaskImage:
                                    'linear-gradient(to right, #000 calc(100% - 24px), transparent)',
                            }}
                        >
                            {summary.composition.map(({ modality, count }) => (
                                <ModalityChip
                                    key={modality}
                                    modality={modality}
                                    count={count}
                                    countPlacement="after"
                                />
                            ))}
                        </Box>
                        <Typography
                            variant="monoMeta"
                            sx={{
                                fontSize: 13,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {intl.formatMessage(
                                { id: 'patientList.drilldown.studyCount' },
                                { count: summary.studyCount }
                            )}
                            {', '}
                            {intl.formatMessage(
                                {
                                    id: 'patientList.drilldown.datasetCount',
                                },
                                { count: summary.datasetCount }
                            )}
                        </Typography>
                    </>
                )}
            </Box>
            {actions && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexShrink: 0,
                    }}
                >
                    {actions}
                </Box>
            )}
        </Box>
    );
}
